import { Link } from "react-router-dom";
import { X, Scale, ArrowRight } from "lucide-react";
import { useCatalog } from "../lib/useCatalog";
import { useHardware } from "../context/HardwareContext";
import { evaluate, recommendedQuant, weightsGB } from "../lib/compute";
import { VerdictBadge } from "../components/VerdictBadge";
import { InstallCommands } from "../components/InstallCommands";

function Row({ label, children }) {
  return (
    <>
      <div className="border-b border-[#1E2638] py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      {children}
    </>
  );
}

export default function Compare() {
  const { data } = useCatalog();
  const { hardware, compare, toggleCompare } = useHardware();

  if (!data) return <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 text-muted-foreground">Chargement…</div>;

  const models = compare.map((id) => data.models.find((m) => m.id === id)).filter(Boolean);
  const cols = models.length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
      <div className="mb-6 flex items-center gap-2">
        <Scale className="h-6 w-6 text-primary" />
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Comparateur</h1>
      </div>

      {cols === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-[#1E2638] bg-[#0F141C] py-16 text-center">
          <p className="text-muted-foreground">Aucun modèle sélectionné. Cochez la case comparateur sur les cartes.</p>
          <Link to="/catalogue" className="mt-3 flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            Aller au catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1E2638] bg-[#0F141C] p-4">
          <div className="grid gap-x-6" style={{ gridTemplateColumns: `160px repeat(${cols}, minmax(200px, 1fr))` }}>
            <div className="border-b border-[#1E2638] py-3" />
            {models.map((m) => (
              <div key={m.id} className="relative border-b border-[#1E2638] py-3">
                <button onClick={() => toggleCompare(m.id)} className="absolute right-0 top-3 text-muted-foreground hover:text-red-400" data-testid={`compare-remove-${m.id}`}>
                  <X className="h-4 w-4" />
                </button>
                <div className="font-head text-base font-bold">{m.name}</div>
                <div className="font-mono2 text-xs text-muted-foreground">{m.family}</div>
              </div>
            ))}

            <Row label="Quantif. recommandée">
              {models.map((m) => {
                const q = recommendedQuant(m, hardware, data.quantizations);
                return <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-sm">{q.label}</div>;
              })}
            </Row>

            <Row label="Verdict">
              {models.map((m) => {
                const q = recommendedQuant(m, hardware, data.quantizations);
                return <div key={m.id} className="border-b border-[#1E2638] py-3"><VerdictBadge verdict={evaluate(m, q, hardware).verdict} /></div>;
              })}
            </Row>

            <Row label="Mémoire requise">
              {models.map((m) => {
                const q = recommendedQuant(m, hardware, data.quantizations);
                return <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-sm">~{evaluate(m, q, hardware).requiredGB.toFixed(1)} Go</div>;
              })}
            </Row>

            <Row label="Vitesse estimée">
              {models.map((m) => {
                const q = recommendedQuant(m, hardware, data.quantizations);
                const t = evaluate(m, q, hardware).tokPerSec;
                return <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-sm">{t > 0 ? `~${t} tok/s` : "—"}</div>;
              })}
            </Row>

            <Row label="Poids FP16">
              {models.map((m) => (
                <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-sm">{weightsGB(m.params, 16).toFixed(1)} Go</div>
              ))}
            </Row>

            <Row label="Contexte">
              {models.map((m) => (
                <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-sm">{(m.context / 1000).toFixed(0)}k</div>
              ))}
            </Row>

            <Row label="Usages">
              {models.map((m) => (
                <div key={m.id} className="border-b border-[#1E2638] py-3 text-sm">{m.use_cases.join(", ")}</div>
              ))}
            </Row>

            <Row label="Licence">
              {models.map((m) => (
                <div key={m.id} className="border-b border-[#1E2638] py-3 font-mono2 text-xs">{m.license}</div>
              ))}
            </Row>

            <Row label="Installation">
              {models.map((m) => (
                <div key={m.id} className="py-3"><InstallCommands model={m} /></div>
              ))}
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}
